import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import type { DiscoverSignUpsRowResponse } from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import { DiscoverBreadcrumbs } from "features/discover/DiscoverBreadcrumbs";
import { createSortArrows, formatDate, translateDiscoverRole } from "features/discover/functions";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { DiscoverRoleChoices } from "features/discover/types";
import { isString } from "remeda";

const statusOptions = [{ value: "true", label: "Finished" }, { value: "false", label: "Unfinished" }];

export function DiscoverSignUpsPage() {
	const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
		pageIndex: 0,
		pageSize: 15,
	});
	const defaultStatus = statusOptions[1];

	const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({ status: defaultStatus.value });
	const [filterValues, setFilterValues] = useState<{ [key: string]: string | undefined }>({ status: defaultStatus.value });
	const [sort, setSort] = useState<{ field: string, direction: string }>();

	useEffect(() => { setScrollBehavior() }, [])

	const { data, isLoading } = client.getDiscoverSignUps.useQuery({
		page: pagination.pageIndex + 1, limit: pagination.pageSize, filters: [
			...(filters?.id ? [{
				field: "id",
				type: "exact",
				value: filters.id,
			}] : []),
			...(filters?.userId ? [{
				field: "userId",
				type: "search",
				value: filters.userId,
			}] : []),
			...(filters?.status ? [{
				field: "user",
				type: "exists",
				value: filters.status === "true",
			}] : []),
			...(filters?.name ? [{
				field: "name",
				type: "search",
				value: filters.name,
			}] : []),
			...(filters?.clubName ? [{
				field: "clubName",
				type: "search",
				value: filters.clubName,
			}] : []),
			...(filters?.email ? [{
				field: "email",
				type: "search",
				value: filters.email,
			}] : []),
			...(filters?.phoneNumber ? [{
				field: "phoneNumber",
				type: "search",
				value: filters.phoneNumber,
			}] : []),
			...(filters?.role ? [{
				field: "role",
				type: "exact",
				value: filters.role,
			}] : []),
			...(filters?.country ? [{
				field: "country",
				type: "exact",
				value: filters.country,
			}] : []),
		],
		order: sort
	});

	const { data: countries, isLoading: isCountriesLoading } = client.getDiscoverCountries.useQuery({});

	const handleSetFilter = (key: string, value?: number | string,) => {
		setFilters({ ...filters, [key]: value });
		setPagination({ ...pagination, pageIndex: 0 });
	}

	const handleClearFilter = (key: string) => {
		setFilters({ ...filters, [key]: undefined })
	}

	const handleSort = (newSort?: { field: string, direction: string }) => {
		setSort(newSort);
	}

	const generateColumnHeader = (columnName: string, label: string, isNumber?: boolean, className?: string, noSort?: boolean) => () => (
		<div className={`tableHeader ${className}`}>
			<TableTextFilter
				value={filterValues[columnName] || ""}
				onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
				number={isNumber}
				clear={() => {
					handleClearFilter(columnName);
					setFilterValues({ ...filterValues, [columnName]: undefined })
				}}
				onSubmit={(value: string) => handleSetFilter(columnName, value)}
				placeholder={label}
			/>
			{!noSort && createSortArrows(columnName, sort, handleSort)}
		</div>
	);

	const simpleColumns: SimpleColumn<DiscoverSignUpsRowResponse>[] = [
		{
			id: "id",
			key: "id",
			header: generateColumnHeader("id", "ID", true, "tableHeader--id", true),
			cell: (value, row) => `${value || ''}`,
		},
		{
			id: "lastName",
			key: "lastName",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={statusOptions.find((o) => o.value === filters?.status)}
					handleChange={(value) => handleSetFilter("status", value?.value)}
					placeholder="Type"
					items={statusOptions}
					isCleareable={false}
				/>
			</div>,
			cell: (value, row) => <div className="thinCell">{row.userId !== null ? 'Finished' : 'Unfinished'}</div>,
		},
		{
			id: "userId",
			key: "userId",
			header: generateColumnHeader("userId", "User ID", true, "tableHeader--id", true),
			cell: (value, row) => `${value || ''}`,
		},
		{
			id: "name",
			key: "firstName",
			header: generateColumnHeader("name", "Name", false, '', true),
			cell: (value, row) => `${row.firstName} ${row.middleName || ''} ${row.lastName}`,
		},
		{
			id: "email",
			key: "email",
			header: generateColumnHeader("email", "email", false, '', true),
			cell: (value, row) => <div className="thinCell">{row.email ?? "-"}</div>,
		},
		{
			id: "phoneNumber",
			key: "phoneNumber",
			header: generateColumnHeader("phoneNumber", "phone", true, '', true),
			cell: (value, row) => <div className="thinCell">{row.phoneNumber ?? "-"}</div>,
		},
		{
			id: "role",
			key: "role",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={DiscoverRoleChoices.find((o) => o.value === filters?.role)}
					handleChange={(value) => handleSetFilter("role", value?.value)}
					placeholder="Role"
					items={DiscoverRoleChoices}
				/>
			</div>,
			cell: (value) => <div className="thinCell">{isString(value) ? translateDiscoverRole(value) : '-'}</div>,
		},
		{
			id: "country",
			key: "country",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={(countries?.data || []).find((o) => o.value === filters?.country)}
					handleChange={(value) => handleSetFilter("country", value?.value)}
					placeholder="Country"
					items={countries?.data || []}
					isLoading={isCountriesLoading}
				/>
			</div>,
			cell: (value) => <div className="thinCell">{countries?.data.find((c) => c.value === value)?.label || value}</div>,
		},
		{
			id: "clubName",
			key: "clubName",
			header: generateColumnHeader("clubName", "Club Name", false, '', true),
			cell: (value, row) => <div className="thinCell">{value}</div>,
		},
		{
			id: "birthday",
			key: "birthday",
			header: () => <div className="tableHeader">Birthday</div>,
			cell: (value, row) => <div className="thinCell">{formatDate(value as string | null)}</div>,
		},
		{
			id: "createdAt",
			key: "createdAt",
			header: () => <div className="tableHeader">
				<span>Created at</span>
				{createSortArrows("createdAt", sort, handleSort)}
			</div>,
			cell: (value, row) => <div className="thinCell"><div>{formatDate(value as string | null)}</div></div>,
		},
	]

	return (
		<div className="discover-container discover-container--wide">
			<div className="pageHeading">
				<DiscoverBreadcrumbs items={[
					{
						label: 'Sing Ups(s)',
					},
				]} />
			</div>
			<div className="discover-card discover-card--for-table">
				<DatagridV2
					getRowLink={(row) => `/discover/sign-up/${row.id}`}
					isLoading={isLoading}
					simpleColumns={simpleColumns}
					data={data?.data.items ?? []}
					pageCount={data?.data?.pages ?? 1}
					pagination={pagination}
					setPagination={setPagination}
					noResults={_("datatables.zero_records")}
				/>
			</div>
		</div>
	)
}
